import { useMutation } from '@apollo/client';
import { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { AUTH_CONSTANTS } from '../constants/auth.constants';
import { AuthContext } from '../context/AuthContext';
import { useWebSocketLogout } from '../context/WebsocketContext';
import { LOGOUT_MUTATION } from '../graphql/resolvers/userMutations/logout.gql';
import type { UserState } from '../interfaces/user.interface';

/**
 * TRIL-146: canonical logout trigger consumed by both the NavigationRail
 * (icon-rail Logout item) and the AvatarMenu (Sign out dropdown item).
 *
 * Internals match what the standalone `<Logout />` button has done since
 * TRIL-130: fire the LOGOUT_MUTATION (server-side refresh-token invalidation),
 * tear down the WebSocket, then call AuthContext.logout() for the full
 * three-layer client cleanup (Redux + Apollo + localStorage + navigate).
 *
 * onError path mirrors onCompleted so a server-unreachable logout still
 * clears client state — the user is logged out from their perspective
 * regardless of network.
 */
export function useLogoutFlow(): () => Promise<void> {
  const { logout } = useContext(AuthContext);
  const { disconnectWebSocket } = useWebSocketLogout();
  const user = useSelector((state: UserState) => state.userStore.user);

  const [logoutUser] = useMutation(LOGOUT_MUTATION, {
    onCompleted: async ({ logoutUser: result }) => {
      if (result?.message != null) {
        disconnectWebSocket();
        await logout();
      }
    },
    onError: async (error) => {
      console.error('LOGOUT_MUTATION failed:', error);
      disconnectWebSocket();
      await logout();
    },
  });

  return useCallback(async () => {
    const refreshToken =
      (localStorage.getItem(AUTH_CONSTANTS.JWT_REFRESH_TOKEN as string) as
        | string
        | null) ?? '';
    await logoutUser({ variables: { refreshToken, userId: user.id } });
  }, [logoutUser, user.id]);
}
